import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Chip, { ChipProps } from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  InvoiceCategoryFilter,
  SettlementState,
  SettlementStateEvent,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Settlement } from '~hooks/useSettlements/models';
import { SettlementStateTransition } from '~hooks/useSettlements/useSettlements';
import { getSettlementStateEventLabel } from '~pages/Approvals/DriverPay/components/helpers';
import { dateFormat } from '~utils/dateTime';
import isEnumValue from '~utils/isEnumValue';
import { getSettlementFor } from '~utils/settlements/settlement-utils';

import { canVoidSettlement } from '../helper';
import { formatDeliveredQuantities } from '../settlementsDataGridColumnsDefinition';
import { formatAccountingAmount, SHEET_DATE_FORMAT } from './settlementSheetDisplay';

const statusChipBySettlementState = {
  [SettlementState.APPROVED]: { color: 'info', labelKey: 'status.approved' },
  [SettlementState.CUSTOMER_APPROVED]: { color: 'info', labelKey: 'status.approved' },
  [SettlementState.CUSTOMER_PAID]: { color: 'success', labelKey: 'status.paid' },
  [SettlementState.CUSTOMER_PENDING]: { color: 'default', labelKey: 'status.draft' },
  [SettlementState.PAID]: { color: 'success', labelKey: 'status.paid' },
  [SettlementState.PENDING]: { color: 'default', labelKey: 'status.draft' },
  [SettlementState.VOIDED]: { color: 'error', labelKey: 'status.voided' },
} satisfies Record<SettlementState, { color: ChipProps['color']; labelKey: string }>;

const HEADER_STATE_TRANSITIONS: SettlementStateTransition[] = [
  SettlementStateEvent.APPROVE,
  SettlementStateEvent.PAY,
  SettlementStateEvent.CUSTOMER_PAY,
];

const getSubtitle = (settlement: Settlement) => {
  const date = dateFormat(settlement.createdAt, SHEET_DATE_FORMAT);
  const quantities = formatDeliveredQuantities(settlement.deliveredQuantities).join(', ');

  return quantities
    ? t('approvals.driver_pay.settlements.sheet.created_with_quantities', {
        date,
        quantities,
      })
    : t('approvals.driver_pay.settlements.sheet.created', { date });
};

interface SettlementSheetHeaderProps {
  areActionsDisabled: boolean;
  category?: InvoiceCategoryFilter;
  currencyFormatter: (value: number) => string;
  isCloseDisabled: boolean;
  isTransitioning: boolean;
  onClose: () => void;
  onTransition: (transition: SettlementStateTransition) => void;
  onVoidClick: () => void;
  settlement: Settlement;
  settlementName: string;
  total: number;
}

export default function SettlementSheetHeader({
  areActionsDisabled,
  category,
  currencyFormatter,
  isCloseDisabled,
  isTransitioning,
  onClose,
  onTransition,
  onVoidClick,
  settlement,
  settlementName,
  total,
}: SettlementSheetHeaderProps) {
  const statusChip = isEnumValue(SettlementState, settlement.state)
    ? statusChipBySettlementState[settlement.state]
    : null;
  const transition = HEADER_STATE_TRANSITIONS.find((stateEvent) =>
    settlement.stateEvents.includes(stateEvent),
  );

  return (
    <Box
      sx={(theme) => ({
        alignItems: 'flex-start',
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        gap: 2,
        justifyContent: 'space-between',
        p: 2,
      })}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {settlementName}
          </Typography>
          {statusChip && (
            <Chip color={statusChip.color} label={t(statusChip.labelKey)} size="small" />
          )}
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {getSettlementFor(settlement, category).name}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {getSubtitle(settlement)}
        </Typography>
      </Box>
      <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 1.5 }}>
        <Box sx={{ textAlign: 'right' }}>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', display: 'block', lineHeight: 1 }}
          >
            {t('common.total')}
          </Typography>
          <Typography
            data-test-id="settlement-sheet-total"
            variant="h5"
            sx={{ fontWeight: 700 }}
          >
            {formatAccountingAmount(total, currencyFormatter)}
          </Typography>
        </Box>
        {canVoidSettlement(settlement) && (
          <SmallButton
            color="brandV2Red"
            disabled={areActionsDisabled}
            onClick={onVoidClick}
            variant="outlined"
          >
            {t('actions.void')}
          </SmallButton>
        )}
        {transition && (
          <SmallLoadingButton
            color="brandV2Green"
            disabled={areActionsDisabled}
            loading={isTransitioning}
            onClick={() => onTransition(transition)}
          >
            {getSettlementStateEventLabel(transition)}
          </SmallLoadingButton>
        )}
        <IconButton
          aria-label={t('actions.close')}
          disabled={isCloseDisabled}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </Box>
    </Box>
  );
}
